import { useState, useEffect } from 'react';
import { authHeaders } from '../services/auth';
import { kvGet, kvPut } from '../services/cloudStorage';
import './AdminPanel.css';

const WORKER_URL = process.env.REACT_APP_WORKER_URL || '';
const ROLE_LABELS  = { admin:'ADMIN', operator:'OPERATORE', readonly:'READ-ONLY' };
const ROLE_OPTIONS = ['admin','operator','readonly'];

export default function AdminPanel({ onClose }) {
  const [users, setUsers]   = useState([]);
  const [loading, setLoad]  = useState(true);
  const [msg, setMsg]       = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState({ username:'', password:'', role:'operator' });
  const [createError, setCreateError] = useState('');
  const [pwEdit, setPwEdit]   = useState(null);
  const [pwVal, setPwVal]     = useState({});
  const [groupName, setGroupNameLocal] = useState('');
  const [groupNameStatus, setGroupNameStatus] = useState('');

  const loadUsers = async () => {
    setLoad(true);
    try {
      const r = await fetch(`${WORKER_URL}/api/admin/users`, { headers: authHeaders(), credentials: 'include' });
      const d = await r.json();
      setUsers(d.users || []);
    } catch { setMsg('Errore caricamento'); }
    setLoad(false);
  };

  useEffect(() => { loadUsers(); }, []);
  useEffect(() => {
    kvGet('group:group_settings').then(s => { if (s?.groupName) setGroupNameLocal(s.groupName); });
  }, []);

  const saveGroupName = async () => {
    const current = await kvGet('group:group_settings') || {};
    await kvPut('group:group_settings', { ...current, groupName: groupName.trim() });
    setGroupNameStatus('✓ Nome salvato');
    setTimeout(() => setGroupNameStatus(''), 2000);
  };

  const addToContacts = async (username, role) => {
    const contacts = await kvGet('group:contacts') || [];
    if (!contacts.find(c => c.name.toLowerCase() === username.toLowerCase())) {
      await kvPut('group:contacts', [...contacts, { id: Date.now(), name: username, phone: '', email: '', notes: `Ruolo: ${role}` }]);
    }
  };

  const approve = async (userId, role, username) => {
    await fetch(`${WORKER_URL}/api/admin/users/${userId}/approve`, {
      method:'PUT', headers:{'Content-Type':'application/json',...authHeaders()},credentials:'include',
      body: JSON.stringify({ role }),
    });
    if (username) await addToContacts(username, role);
    setMsg('✓ Approvato'); loadUsers();
  };

  const changeRole = async (userId, role) => {
    await fetch(`${WORKER_URL}/api/admin/users/${userId}/role`, {
      method:'PUT', headers:{'Content-Type':'application/json',...authHeaders()},credentials:'include',
      body: JSON.stringify({ role }),
    });
    setMsg('✓ Ruolo aggiornato'); loadUsers();
  };

  const deleteUser = async (userId, username) => {
    if (!window.confirm(`Eliminare "${username}"?`)) return;
    await fetch(`${WORKER_URL}/api/admin/users/${userId}`, {
      method:'DELETE', headers: authHeaders(),
    });
    setMsg('✓ Eliminato'); loadUsers();
  };

  const createUser = async () => {
    setCreateError('');
    const { username, password, role } = newUser;
    if (!username || !password) { setCreateError('Compila tutti i campi'); return; }
    if (username.length < 3 || username.length > 32) { setCreateError('Username: 3-32 caratteri'); return; }
    if (!/^[a-zA-Z0-9_.\-]+$/.test(username)) { setCreateError('Username: solo lettere, numeri, _ . -'); return; }
    if (password.length < 12)      { setCreateError('Password: minimo 12 caratteri'); return; }
    if (!/[A-Z]/.test(password))   { setCreateError('Password: almeno una lettera maiuscola'); return; }
    if (!/[a-z]/.test(password))   { setCreateError('Password: almeno una lettera minuscola'); return; }
    if (!/[0-9]/.test(password))   { setCreateError('Password: almeno un numero'); return; }
    if (!/[^A-Za-z0-9]/.test(password)) { setCreateError('Password: almeno un simbolo (!@#$…)'); return; }
    try {
      const r1 = await fetch(`${WORKER_URL}/api/auth/register`, {
        method:'POST', headers:{'Content-Type':'application/json',...authHeaders()},credentials:'include',
        body: JSON.stringify({ username, password }),
      });
      const d1 = await r1.json();
      if (!r1.ok) { setCreateError(d1.error || 'Errore registrazione'); return; }
      await fetch(`${WORKER_URL}/api/admin/users/${d1.userId}/approve`, {
        method:'PUT', headers:{'Content-Type':'application/json',...authHeaders()},credentials:'include',
        body: JSON.stringify({ role }),
      });
      await addToContacts(username, role);
      setMsg('✓ Utente creato e approvato');
      setNewUser({ username:'', password:'', role:'operator' });
      setCreateError('');
      setShowCreate(false);
      loadUsers();
    } catch { setCreateError('Errore di rete — riprova'); }
  };

  const changePassword = async (userId) => {
    const pw = pwVal[userId] || '';
    if (pw.length < 12) { setMsg('⚠ Password: minimo 12 caratteri'); return; }
    try {
      const r = await fetch(`${WORKER_URL}/api/admin/users/${userId}/password`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ password: pw }),
      });
      const d = await r.json();
      if (!r.ok) { setMsg(`⚠ ${d.error}`); return; }
      setMsg('✓ Password aggiornata');
      setPwEdit(null); setPwVal({});
    } catch { setMsg('⚠ Errore cambio password'); }
  };

  const pending  = users.filter(u => !u.approved);
  const approved = users.filter(u =>  u.approved);
  const fmtDate  = ts => ts ? new Date(ts).toLocaleDateString('it-IT') : '—';

  return (
    <div className="admin-panel">
      <div className="admin-hdr">
        <span>// SETUP — UTENTI</span>
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          <button className="btn-new-user" onClick={()=>{ setShowCreate(v=>!v); setCreateError(''); }}>
            {showCreate ? '✕ CHIUDI' : '+ NUOVO UTENTE'}
          </button>
          <span className="admin-close" onClick={onClose}>[X]</span>
        </div>
      </div>

      {msg && <div className="admin-msg" onClick={()=>setMsg('')}>{msg}</div>}

      {/* Nome Gruppo */}
      <div className="admin-section" style={{paddingBottom:8}}>
        <div className="admin-sec-label">// NOME GRUPPO</div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <input className="field-inp" placeholder="nome del gruppo..." value={groupName}
            onChange={e=>setGroupNameLocal(e.target.value)}
            onKeyDown={e=>e.key==='Enter'&&saveGroupName()}
            style={{flex:1,fontSize:'13px',padding:'6px 4px'}}/>
          <button className="btn-approve" onClick={saveGroupName}>✓</button>
        </div>
        {groupNameStatus && <div style={{fontSize:'11px',color:'#00ff41',marginTop:4}}>{groupNameStatus}</div>}
      </div>

      {/* Form crea utente */}
      {showCreate && (
        <div className="create-user-form">
          <div className="form-label">// CREA NUOVO UTENTE</div>
          <div className="cu-grid">
            <div className="form-field">
              <span className="field-lbl">USERNAME</span>
              <input className="field-inp" placeholder="username..." value={newUser.username}
                onChange={e=>setNewUser(v=>({...v,username:e.target.value}))}/>
            </div>
            <div className="form-field">
              <span className="field-lbl">PASSWORD</span>
              <input className="field-inp" type="password" placeholder="min 12 car., maiusc., numero, simbolo" value={newUser.password}
                onChange={e=>{ setNewUser(v=>({...v,password:e.target.value})); setCreateError(''); }}/>
            </div>
            <div className="form-field">
              <span className="field-lbl">RUOLO</span>
              <select className="role-sel" value={newUser.role} onChange={e=>setNewUser(v=>({...v,role:e.target.value}))}>
                {ROLE_OPTIONS.map(r=><option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </div>
          </div>
          <button className="form-submit" onClick={createUser}>+ CREA UTENTE</button>
          {createError && <div className="create-error">⚠ {createError}</div>}
        </div>
      )}

      {loading ? <div className="admin-loading">caricamento...</div> : (
        <>
          {pending.length > 0 && (
            <div className="admin-section">
              <div className="admin-sec-label">⚡ IN ATTESA ({pending.length})</div>
              {pending.map(u=>(
                <div className="user-row pending" key={u.id}>
                  <div className="user-info">
                    <div className="user-uname">{u.username}</div>
                    <div className="user-meta">registrato {fmtDate(u.createdAt)}</div>
                  </div>
                  <div className="user-actions">
                    <select className="role-sel" defaultValue="operator" id={`sel-${u.id}`}>
                      {ROLE_OPTIONS.map(r=><option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                    </select>
                    <button className="btn-approve" onClick={()=>{
                      const sel=document.getElementById(`sel-${u.id}`);
                      approve(u.id,sel?.value||'operator',u.username);
                    }}>✓ APPROVA</button>
                    <button className="btn-delete" onClick={()=>deleteUser(u.id,u.username)}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="admin-section">
            <div className="admin-sec-label">// UTENTI ATTIVI ({approved.length})</div>
            {approved.map(u=>(
              <div key={u.id}>
                <div className="user-row">
                  <div className="user-info">
                    <div className="user-uname">
                      {u.username}
                      <span className={`role-tag role-${u.role}`}>{ROLE_LABELS[u.role]}</span>
                    </div>
                    <div className="user-meta">ultimo accesso: {fmtDate(u.lastLogin)}</div>
                  </div>
                  <div className="user-actions">
                    <select className="role-sel" value={u.role} onChange={e=>changeRole(u.id,e.target.value)}>
                      {ROLE_OPTIONS.map(r=><option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                    </select>
                    <button className="btn-pw" onClick={()=>setPwEdit(v=>v===u.id?null:u.id)} title="Cambia password">🔑</button>
                    <button className="btn-delete" onClick={()=>deleteUser(u.id,u.username)}>✕</button>
                  </div>
                </div>
                {pwEdit===u.id && (
                  <div className="pw-edit-row">
                    <input type="password" className="field-inp" placeholder="nuova password (min 12, maiusc, num, simbolo)..."
                      value={pwVal[u.id]||''} onChange={e=>setPwVal(p=>({...p,[u.id]:e.target.value}))}
                      onKeyDown={e=>e.key==='Enter'&&changePassword(u.id)}/>
                    <button className="btn-approve" onClick={()=>changePassword(u.id)}>✓</button>
                    <button className="btn-cancel" onClick={()=>{setPwEdit(null);setPwVal({})}}>✕</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
